import FixaCheckbox from "@coworker/apprestructured/src/shared/wrappers/FixaCheckbox/FixaCheckbox";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { LogsAndComments } from "@coworker/apprestructured/src/task-management/components/LogsAndComments/LogsAndComments";
import { NotificationsProvider } from "@coworker/apprestructured/src/task-management/components/Notifications/NotificationsContext";
import { useNotificationsState } from "@coworker/apprestructured/src/task-management/components/Notifications/hooks/useNotificationsState";
import Actions from "@coworker/apprestructured/src/task-management/demo/pages/ComponentDemoPage/demos/Actions/Actions";
import { Loading } from "@coworker/apprestructured/src/task-management/loading";
import { memo, useCallback, useMemo, useState } from "react";
import Demo from "./Demo/Demo";
import Highlight from "./Highlight/Highlight";
import Name from "./Name/Name";

function LogsAndCommentsDemo() {
  const [logTitle, setLogTitle] = useState("Oliver Kellgren");
  const [logContent, setLogContent] = useState(
    "Created & assigned task to Oliver Kellgren"
  );
  const [logTime, setLogTime] = useState("2024-12-23");
  const [commentTitle, setCommentTitle] = useState("John Lovén");
  const [commentContent, setCommentContent] = useState(
    "This is some note that I created."
  );
  const [commentTime, setCommentTime] = useState("Just now");
  const [nbrOfLogItems, setNbrOfLogItems] = useState("5");
  const [nbrOfCommentItems, setNbrOfCommentItems] = useState("5");
  const [addCommentText, setAddCommentText] = useState("");
  const [addCommentImages, setAddCommentImages] = useState<File[]>([]);
  const [customComments, setCustomComments] = useState<
    Array<{
      id: string;
      title: string;
      content: string;
      time: string;
      imageUrls: string[] | Loading;
      imageCreateUploads: {
        name: string;
        size: number;
        bucket_id: string;
      }[];
      creatorUserId: string;
    }>
  >([]);
  const [disableAllAddCommentInputs, setDisableAllAddCommentInputs] =
    useState(false);
  const [disableAddCommentButton, setDisableAddCommentButton] = useState(false);

  const notifications = useNotificationsState();

  const logsTabItems = useMemo(() => {
    return Array.from({ length: parseInt(nbrOfLogItems) }).map(() => ({
      title: logTitle,
      content: logContent,
      time: logTime,
    }));
  }, [logContent, logTime, logTitle, nbrOfLogItems]);

  const commentsTabItems = useMemo(() => {
    const demoComments = Array.from({
      length: parseInt(nbrOfCommentItems),
    }).map(() => ({
      id: "123",
      title: commentTitle,
      content: commentContent,
      time: commentTime,
      imageUrls: [],
      imageCreateUploads: [],
      creatorUserId: "123",
    }));
    return [...demoComments, ...customComments];
  }, [
    commentContent,
    commentTime,
    commentTitle,
    nbrOfCommentItems,
    customComments,
  ]);

  const handleAddCommentClick = useCallback(() => {
    setCustomComments([
      ...customComments,
      {
        id: "123",
        title: commentTitle,
        content: addCommentText,
        time: commentTime,
        imageUrls: [],
        imageCreateUploads: [],
        creatorUserId: "123",
      },
    ]);
    setAddCommentText("");
    setAddCommentImages([]);
  }, [addCommentText, commentTitle, commentTime, customComments]);

  return (
    <Demo>
      <NotificationsProvider value={notifications}>
        <Name>LogsAndComments</Name>
        <Highlight>
          <FixaListView id="logsandcommentsdemo">
            <LogsAndComments
              taskId="demo-task-id"
              isLoadingComments={false}
              isLoadingLogs={false}
              onTabChanged={() => {}}
              logsTabItems={logsTabItems}
              commentsTabItems={commentsTabItems}
              addCommentText={addCommentText}
              addCommentImages={addCommentImages}
              disableAllAddCommentInputs={disableAllAddCommentInputs}
              disableAddCommentButton={disableAddCommentButton}
              onAddCommentTextChange={setAddCommentText}
              onAddCommentImagesChange={setAddCommentImages}
              onAddCommentClick={handleAddCommentClick}
            />
          </FixaListView>
        </Highlight>
        <Actions>
          <FixaInputField
            label="logTitle"
            type="text"
            id="logsandcommentsdemo-logTitle"
            value={logTitle}
            onChange={(e) => setLogTitle(e.target.value)}
          />
          <FixaInputField
            label="logContent"
            type="text"
            id="logsandcommentsdemo-logContent"
            value={logContent}
            onChange={(e) => setLogContent(e.target.value)}
          />
          <FixaInputField
            label="logTime"
            type="text"
            id="logsandcommentsdemo-logTime"
            value={logTime}
            onChange={(e) => setLogTime(e.target.value)}
          />
          <FixaInputField
            id="logsandcommentsdemo-nbrOfLogItems"
            value={nbrOfLogItems}
            onChange={(e) => setNbrOfLogItems(e.target.value)}
            type="number"
            min="0"
            max="100"
            label="nbrOfLogItems"
          />
          <FixaInputField
            label="commentTitle"
            type="text"
            id="logsandcommentsdemo-commentTitle"
            value={commentTitle}
            onChange={(e) => setCommentTitle(e.target.value)}
          />
          <FixaInputField
            label="commentContent"
            type="text"
            id="logsandcommentsdemo-commentContent"
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
          />
          <FixaInputField
            label="commentTime"
            type="text"
            id="logsandcommentsdemo-commentTime"
            value={commentTime}
            onChange={(e) => setCommentTime(e.target.value)}
          />
          <FixaInputField
            id="logsandcommentsdemo-nbrOfCommentItems"
            value={nbrOfCommentItems}
            onChange={(e) => setNbrOfCommentItems(e.target.value)}
            type="number"
            min="0"
            max="100"
            label="nbrOfCommentItems"
          />
          <FixaCheckbox
            label="disableAllAddCommentInputs"
            value="logsandcommentsdemo-disableAllAddCommentInputs"
            id="logsandcommentsdemo-disableAllAddCommentInputs"
            checked={disableAllAddCommentInputs}
            onChange={() => setDisableAllAddCommentInputs((prev) => !prev)}
          />
          <FixaCheckbox
            label="disableAddCommentButton"
            value="logsandcommentsdemo-disableAddCommentButton"
            id="logsandcommentsdemo-disableAddCommentButton"
            checked={disableAddCommentButton}
            onChange={() => setDisableAddCommentButton((prev) => !prev)}
          />
        </Actions>
      </NotificationsProvider>
    </Demo>
  );
}

export default memo(LogsAndCommentsDemo);
